import { Link, useLocation } from "react-router-dom"
import logo from "/logo-transparent.png"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from "@/components/ui/sidebar"
import { Bill, Book, Buildings2, Buildings3, Calendar, ChatSquare, ClockCircle, Help, Layers, Notebook, Settings, UsersGroupRounded, VolumeLoud, Wallet, Widget } from "@solar-icons/react"
import {
  ADMIN_HOME,
  ADMIN_BUSINESSES,
  ADMIN_PENDING_APPROVALS,
  ADMIN_SUPPORT,
  ADMIN_ADMINS,
  ADMIN_MESSAGING,
  ADMIN_TRANSACTIONS,
  ADMIN_PAYOUTS,
  ADMIN_AD_PLANS,
  ADMIN_BUSINESS_PLANS,
  ADMIN_INDUSTRIES,
  ADMIN_SETTINGS,
  ADMIN_SERVICES,
  ADMIN_PROVIDERS,
  ADMIN_AD_CAMPAIGNS,
  ADMIN_PENDING_AD_CAMPAIGNS,
  ADMIN_APPOINTMENTS,
  ADMIN_CUSTOMERS,
} from "@/constants/paths/admin"
import { useTranslation } from "react-i18next"

const adminNavItems = [
  { titleKey: "dashboard", url: ADMIN_HOME, icon: Widget, stepId: "admin-welcome dashboard" },
  { titleKey: "businesses", url: ADMIN_BUSINESSES, icon: Buildings2, stepId: "admin-businesses" },
  { titleKey: "services", url: ADMIN_SERVICES, icon: ClockCircle, stepId: "admin-services" },
  { titleKey: "providers", url: ADMIN_PROVIDERS, icon: UsersGroupRounded, stepId: "admin-providers" },
  { titleKey: "adCampaigns", url: ADMIN_AD_CAMPAIGNS, icon: VolumeLoud, stepId: "admin-ad-campaigns" },
  { titleKey: "pendingAdCampaigns", url: ADMIN_PENDING_AD_CAMPAIGNS, icon: VolumeLoud, stepId: "admin-pending-ad-campaigns" },
  { titleKey: "appointments", url: ADMIN_APPOINTMENTS, icon: Calendar, stepId: "admin-appointments" },
  { titleKey: "customers", url: ADMIN_CUSTOMERS, icon: Notebook, stepId: "admin-customers" },
  { titleKey: "pendingApprovals", url: ADMIN_PENDING_APPROVALS, icon: ClockCircle, stepId: "admin-approvals" },
  { titleKey: "helpSupport", url: ADMIN_SUPPORT, icon: Help, stepId: "admin-support" },
  { titleKey: "admins", url: ADMIN_ADMINS, icon: UsersGroupRounded, stepId: "admin-admins" },
  { titleKey: "messaging", url: ADMIN_MESSAGING, icon: ChatSquare, stepId: "admin-messaging" },
  { titleKey: "transactions", url: ADMIN_TRANSACTIONS, icon: Bill, stepId: "admin-transactions" },
  { titleKey: "payouts", url: ADMIN_PAYOUTS, icon: Wallet, stepId: "admin-payouts" },
  { titleKey: "adPlans", url: ADMIN_AD_PLANS, icon: VolumeLoud, stepId: "admin-ad-plans" },
  { titleKey: "businessPlans", url: ADMIN_BUSINESS_PLANS, icon: Layers, stepId: "admin-business-plans" },
  { titleKey: "industries", url: ADMIN_INDUSTRIES, icon: Buildings3, stepId: "admin-industries" },
  { titleKey: "settings", url: `${ADMIN_SETTINGS}/profile`, icon: Settings, stepId: "admin-settings", isSettings: true },
] as const

interface AdminSidebarProps {
  onStartTour?: () => void
}

export function AdminSidebar({ onStartTour }: AdminSidebarProps) {
  const { t } = useTranslation("common")
  const location = useLocation()
  const currentPath = location.pathname

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="py-3 overflow-hidden" data-sidebar="header">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link to={ADMIN_HOME}>
                <img src={logo} alt="Woezor" className="h-14 w-auto object-cover" />
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {adminNavItems.map((item) => {
                const label = t(`adminNav.${item.titleKey}`)
                const isActive =
                  item.titleKey === "dashboard"
                    ? currentPath === "/admin" || currentPath === "/admin/"
                    : "isSettings" in item && item.isSettings
                      ? currentPath.startsWith(ADMIN_SETTINGS)
                      : currentPath.startsWith(item.url)

                return (
                  <SidebarMenuItem key={item.titleKey}>
                    <SidebarMenuButton
                      asChild
                      isActive={isActive}
                      tooltip={label}
                      data-tour-step-id={item.stepId}
                    >
                      <Link to={item.url}>
                        <item.icon className="size-4" />
                        <span>{label}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        {onStartTour ? (
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton onClick={onStartTour} tooltip={t("nav.tutorialGuide")}>
                <Book className="text-primary" />
                <span>{t("nav.tutorialGuide")}</span>
                <span className="relative ml-auto flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                </span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        ) : null}
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  )
}
